import { userManagementApis } from "@services/apis/UserManagement";
import {
  AuthorizationSettings,
  GetSecuritySettingsResponse,
  SecuritySettingData,
} from "@services/models/UserManagement/get-security-settings";
import { UpdateSecuritySettingRequest } from "@services/models/UserManagement/update-security-settings";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSnackbarMessage from "../../../../utils/HandleSuccessErrorSnackbars";
import SettingsTableRow from "./SettingsTableRow";

interface SecurityAccountProps {
  userId: number;
  settingsData: SecuritySettingData[] | undefined;
  loading: boolean;
  is2FaEnabled: boolean;
  setAccountSettings: React.Dispatch<
    React.SetStateAction<GetSecuritySettingsResponse | null>
  >;
  setEmailOtpRequired: React.Dispatch<React.SetStateAction<boolean>>;
  setGoogle2FaRequired: React.Dispatch<React.SetStateAction<boolean>>;
}

const SecurityAccount: React.FC<SecurityAccountProps> = ({
  userId,
  settingsData,
  loading,
  is2FaEnabled,
  setAccountSettings,
  setEmailOtpRequired,
  setGoogle2FaRequired,
}) => {
  const navigate = useNavigate();
  const [settingIndex, setSettingsIndex] = useState<number>(-1);
  const [isEmailChanged, setIsEmailChanged] = useState<boolean>(false);
  const [isGoogle2FaChanged, setIsGoogle2FaChanged] = useState<boolean>(false);

  const { handleError } = useSnackbarMessage();

  const handleSubmit = async (
    settingFrontEndKey: string,
    emailEnabled: boolean,
    emailValue: boolean,
    google2FAEnabled: boolean | undefined,
    google2FAValue: boolean,
  ) => {
    const data: UpdateSecuritySettingRequest = {
      userId,
      settingFrontEndKey,
      settingValue: {
        email: {
          isEnabled: emailEnabled,
          value: emailValue,
        },
        google2FA: {
          isEnabled: google2FAEnabled ?? false,
          value: google2FAValue,
        },
      },
    };

    try {
      await userManagementApis.updateSecuritySettings(data);
      setAccountSettings((prevSettings) => {
        if (!prevSettings) return null;
        const updatedSettingsData = prevSettings.data.map((setting) => {
          if (setting.frontEndKey === settingFrontEndKey) {
            // Update the relevant setting
            return {
              ...setting,
              settingValue: {
                email: {
                  isEnabled: emailEnabled,
                  value: emailValue,
                },
                google2FA: {
                  isEnabled: google2FAEnabled ?? false,
                  value: google2FAValue,
                },
              },
            };
          }
          return setting;
        });
        return {
          ...prevSettings,
          data: updatedSettingsData,
        };
      });

      if (settingFrontEndKey === "changeSecuritySettings") {
        setEmailOtpRequired(emailValue);
        setGoogle2FaRequired(google2FAValue);
      }

      setIsEmailChanged(false);
      setIsGoogle2FaChanged(false);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "An unknown error occurred";
      handleError({ message: errorMessage });
    }
  };

  const handleEmailChange = (index: number) => {
    if (!settingsData) return;

    const setting = settingsData[index];
    const settingValue = setting.settingValue as AuthorizationSettings;

    handleSubmit(
      setting.frontEndKey,
      settingValue.email.isEnabled,
      !settingValue.email.value, // toggle email
      settingValue.google2FA.isEnabled,
      settingValue.google2FA.value,
    );
  };

  const handleGoogle2FaChange = (index: number) => {
    if (!settingsData) return;

    const setting = settingsData[index];
    const settingValue = setting.settingValue as AuthorizationSettings;

    handleSubmit(
      setting.frontEndKey,
      settingValue.email.isEnabled,
      settingValue.email.value,
      settingValue.google2FA.isEnabled,
      !settingValue.google2FA.value, // toggle 2FA
    );
  };

  useEffect(() => {
    // Check and update if settingsData has relevant data for email and google2FA requirements
    const changeSecuritySetting =
      settingsData &&
      settingsData!.find(
        (setting) => setting.frontEndKey === "changeSecuritySettings",
      );
    if (changeSecuritySetting) {
      setEmailOtpRequired(
        (changeSecuritySetting.settingValue as AuthorizationSettings).email
          .value,
      );
      setGoogle2FaRequired(
        (changeSecuritySetting.settingValue as AuthorizationSettings).google2FA
          .value,
      );
    }
  }, [settingsData, setEmailOtpRequired, setGoogle2FaRequired]);

  return (
    <div className="flex flex-col bg-bg-page-body px-[16px] pb-[16px]">
      <div className="flex flex-col mt-[16px]">
        <table className="min-w-full text-text-base text-[12px]">
          <thead className="bg-bg-header rounded-t-lg">
            <tr>
              <th
                className="p-[16px] text-left text-text-base text-[14px] rounded-tl-lg font-semibold"
                style={{ width: "300px" }}>
                Confirmations
              </th>
              <th className="p-[16px] text-center text-text-base text-[14px] font-semibold">
                Email
              </th>
              <th className="p-[16px] text-center text-text-base text-[14px] rounded-tr-lg font-semibold">
                2FA
              </th>
            </tr>
          </thead>
          <tbody className="min-w-full border border-border-table rounded-b-lg">
            {settingsData
              ? settingsData.map((setting, index) => {
                  const settingValue =
                    setting.settingValue as AuthorizationSettings;
                  return (
                    <SettingsTableRow
                      key={index}
                      title={setting.title}
                      email={settingValue.email.value}
                      google2Fa={settingValue.google2FA.value}
                      rounded={index === settingsData.length - 1}
                      onEmailChange={() => handleEmailChange(index)}
                      onGoogle2FaChange={() => handleGoogle2FaChange(index)}
                      isEmailEnabled={settingValue.email.isEnabled}
                      isGoogle2FaEnabled={
                        is2FaEnabled &&
                        (settingValue.google2FA.isEnabled ?? false)
                      }
                      loading={loading}
                    />
                  );
                })
              : [...Array(2)].map((_, index) => (
                  <SettingsTableRow
                    key={index}
                    title=""
                    email={false}
                    google2Fa={false}
                    rounded={index === 1}
                    isEmailEnabled={false}
                    onEmailChange={() => {}}
                    onGoogle2FaChange={() => {}}
                    isGoogle2FaEnabled={false}
                    loading={true}
                  />
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SecurityAccount;
